import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import styles from './InternalDomainVerificationPanel.module.scss'

import { isApiError } from '@/api'
import {
  checkInternalDomainVerification,
  getInternalDomainVerificationStatus,
  initiateInternalDomainVerification,
} from '@/api/internal'
import Button from '@/components/Button'
import Select from '@/components/Select'
import { useToast } from '@/components/Toast/ToastContext'
import { useEnterpriseDetails } from '@/hooks/useEnterpriseDetails'
import { compactAmPm } from '@/utils/compactAmPm'

export const internalDomainVerificationStatusQueryKey = (domainId: string) =>
  ['internal-domain-verification-status', domainId] as const

interface InternalDomainVerificationPanelProps {
  enterpriseId: string
  onError: (error: string | null) => void
}

const InternalDomainVerificationPanel = ({ enterpriseId, onError }: InternalDomainVerificationPanelProps) => {
  const queryClient = useQueryClient()
  const { updateToast } = useToast()
  const [selectedDomainId, setSelectedDomainId] = useState('')
  const [copiedHost, setCopiedHost] = useState(false)
  const [copiedValue, setCopiedValue] = useState(false)

  const { data: detailsResponse, isLoading: detailsLoading } = useEnterpriseDetails(enterpriseId)
  const domains = detailsResponse?.data.domains ?? []
  const activeDomains = useMemo(() => domains.filter((d) => !d.deleted), [domains])

  useEffect(() => {
    if (activeDomains.length === 0) {
      setSelectedDomainId('')
      return
    }
    setSelectedDomainId((prev) => {
      if (prev && activeDomains.some((d) => d.id === prev)) return prev
      return activeDomains[0].id
    })
  }, [activeDomains])

  const statusQuery = useQuery({
    queryKey: internalDomainVerificationStatusQueryKey(selectedDomainId),
    queryFn: async () => {
      const res = await getInternalDomainVerificationStatus(selectedDomainId)
      return res.data
    },
    enabled: !!selectedDomainId,
  })

  useEffect(() => {
    if (statusQuery.error && isApiError(statusQuery.error)) {
      onError(statusQuery.error.detail || statusQuery.error.message || 'Failed to load verification status')
    }
  }, [statusQuery.error, onError])

  const initiateMutation = useMutation({
    mutationFn: async () => initiateInternalDomainVerification(selectedDomainId),
    onSuccess: async () => {
      onError(null)
      updateToast('Verification token issued (or existing token reused)', 'success')
      await queryClient.invalidateQueries({
        queryKey: internalDomainVerificationStatusQueryKey(selectedDomainId),
      })
    },
    onError: (err: unknown) => {
      if (isApiError(err)) {
        onError(err.detail || err.message || 'Failed to initiate verification')
      } else {
        onError('Failed to initiate verification')
      }
    },
  })

  const checkMutation = useMutation({
    mutationFn: async () => checkInternalDomainVerification(selectedDomainId),
    onSuccess: async (response) => {
      onError(null)
      if (response.data.verified) {
        updateToast('Domain verified successfully', 'success')
      } else {
        updateToast(
          'DNS check finished — no matching TXT yet (customer may still need to publish or propagate)',
          'success'
        )
      }
      await queryClient.invalidateQueries({
        queryKey: internalDomainVerificationStatusQueryKey(selectedDomainId),
      })
    },
    onError: (err: unknown) => {
      if (isApiError(err)) {
        onError(err.detail || err.message || 'Failed to check verification')
      } else {
        onError('Failed to check verification')
      }
    },
  })

  const status = statusQuery.data
  const record = status?.record
  /** Unverified domain already has a DNS TXT token issued — use Check, not Initiate. */
  const hasTxtRecord = !!(status && (status.token_active || !!record))
  const verified = !!status?.verified
  const canInitiate = !!status && !verified && !hasTxtRecord
  const canCheck = !!status && !verified && hasTxtRecord
  const anyMutation = initiateMutation.isPending || checkMutation.isPending

  const copyToClipboard = async (text: string, kind: 'host' | 'value') => {
    try {
      await navigator.clipboard.writeText(text)
      updateToast('Copied to clipboard', 'success')
      if (kind === 'host') {
        setCopiedHost(true)
        window.setTimeout(() => setCopiedHost(false), 2000)
      } else {
        setCopiedValue(true)
        window.setTimeout(() => setCopiedValue(false), 2000)
      }
    } catch {
      updateToast('Could not copy', 'error')
    }
  }

  const domainOptions = useMemo(() => activeDomains.map((d) => ({ value: d.id, label: d.domain })), [activeDomains])

  if (detailsLoading) {
    return (
      <div className={styles.panel}>
        <div className={styles.loading}>Loading domains…</div>
      </div>
    )
  }

  if (activeDomains.length === 0) {
    return (
      <div className={styles.panel}>
        <p className={styles.hint}>
          No active domains on this enterprise. Add a domain in the Domains section above, then return here to manage
          DNS verification.
        </p>
      </div>
    )
  }

  return (
    <div className={styles.panel}>
      <p className={styles.hint}>
        Review verification status for the selected domain. Initiate issues a TXT record when none exists; Check runs
        DNS lookup after the customer publishes it.
      </p>

      <div className={styles.row}>
        <span className={styles.label}>Domain</span>
        <Select
          value={selectedDomainId}
          options={domainOptions}
          onChange={(id) => {
            setSelectedDomainId(id)
            onError(null)
          }}
          className={styles.select}
          isDisabled={anyMutation}
        />
      </div>

      {statusQuery.isLoading && selectedDomainId && <div className={styles.loading}>Loading verification status…</div>}

      {statusQuery.error && !isApiError(statusQuery.error) && (
        <p className={styles.errorBox}>Failed to load verification status.</p>
      )}

      {status && !statusQuery.isLoading && (
        <>
          <div className={styles.statusBlock}>
            <div className={styles.statusHeader}>
              <span className={styles.domainTitle}>{status.domain}</span>
              {status.verified ? (
                <span className={styles.pillVerified}>Verified</span>
              ) : (
                <span className={styles.pillPending}>Not verified</span>
              )}
            </div>
            <p className={styles.meta}>
              Verification initiated:{' '}
              <span>
                {status.verification_initiated_at ? compactAmPm(new Date(status.verification_initiated_at).toLocaleString()) : '—'}
              </span>
            </p>
            <p className={styles.meta}>
              Last DNS check:{' '}
              <span>
                {status.last_verification_check_at ? compactAmPm(new Date(status.last_verification_check_at).toLocaleString()) : '—'}
              </span>
            </p>
            <p className={styles.meta}>
              Verification attempts: <span>{status.verification_attempts}</span>
            </p>
            <p className={styles.meta}>
              Token active: <span>{status.token_active ? 'Yes' : 'No'}</span>
            </p>
          </div>

          {record && (
            <div className={styles.recordBox}>
              <div className={styles.recordLabel}>TXT record (share with customer)</div>
              <div className={styles.codeRow}>
                <pre className={styles.code}>{record.host}</pre>
                <Button
                  title={copiedHost ? 'Copied' : 'Copy host'}
                  type="light"
                  onClick={() => copyToClipboard(record.host, 'host')}
                  isDisabled={anyMutation}
                />
              </div>
              <div className={styles.codeRow}>
                <pre className={styles.code}>{record.value}</pre>
                <Button
                  title={copiedValue ? 'Copied' : 'Copy value'}
                  type="light"
                  onClick={() => copyToClipboard(record.value, 'value')}
                  isDisabled={anyMutation}
                />
              </div>
            </div>
          )}

          <div className={styles.actions}>
            <Button
              title="Initiate verification"
              type="dark"
              onClick={() => initiateMutation.mutate()}
              isLoading={initiateMutation.isPending}
              isDisabled={anyMutation || !canInitiate}
            />
            <Button
              title="Check verification"
              type="dark"
              onClick={() => checkMutation.mutate()}
              isLoading={checkMutation.isPending}
              isDisabled={anyMutation || !canCheck}
            />
          </div>
        </>
      )}
    </div>
  )
}

export default InternalDomainVerificationPanel
